import { ArrowUpRight } from "lucide-react";
import {
  PORTFOLIO_CLEANY,
  PORTFOLIO_SPECTACULAR,
  PORTFOLIO_PRECLOSE,
  PORTFOLIO_ADMACHINE,
} from "@/lib/optimizedImages";

/**
 * RevOps Live-Proof Marquee — continuous right-to-left scrolling row of
 * production-system case-study cards. Sits inside the hero, directly below
 * the CTA, reading as the hero's active proof engine (trusted-badge extension).
 *
 * 6 production systems:
 * 1. Cleany.ca
 * 2. Spectacular Academy
 * 3. Preclose.ai
 * 4. She's Got Goals
 * 5. AdMachine.dev
 * 6. MultiplyMe
 *
 * Each card features:
 * - Real live-site screenshot
 * - Category pill & live signal
 * - Platform tags
 * - Business title
 * - Concrete role description ("What I built / What I do")
 * - Key revenue metric + direct external link
 */

export interface ProofCard {
  category: string;
  signal: string;
  tags: string[];
  title: string;
  role: string;
  metric: string;
  metricLabel: string;
  url: string;
  image: string;
  dark: boolean;
}

export const cards: ProofCard[] = [
  {
    category: "Commercial Services",
    signal: "AUTO-DISPATCH ENGINE",
    tags: ["Franchise Routing", "Live Booking", "Stripe Sync"],
    title: "Cleany.ca Franchise Ops",
    role: "Architected multi-location automated dispatch, real-time booking, Stripe checkout flow, and two-way SMS crew notification systems.",
    metric: "99.4%",
    metricLabel: "Booking Automation Rate",
    url: "https://cleany.ca/",
    image: PORTFOLIO_CLEANY,
    dark: false,
  },
  {
    category: "Education & Academy",
    signal: "STUDENT FUNNEL PIPELINE",
    tags: ["LMS Integration", "Course Checkout", "Student Recall"],
    title: "Spectacular Academy",
    role: "Engineered high-converting curriculum enrollment funnels, automated onboarding workflows, and student retention follow-up sequences.",
    metric: "3.8×",
    metricLabel: "Course Enrolment Lift",
    url: "https://spectacularacademy.com/",
    image: PORTFOLIO_SPECTACULAR,
    dark: true,
  },
  {
    category: "Inbound Funnels & CRO",
    signal: "POST-CLICK CONVERSION",
    tags: ["Sales Call Analysis", "Funnel CRO", "Pre-Call Nurture"],
    title: "Preclose.ai Inbound CRO",
    role: "Architected post-click inbound conversion funnels, pre-call sales nurture workflows, and automated pipeline leak audits.",
    metric: "20-60%",
    metricLabel: "Conversion Lift",
    url: "https://preclose.ai/",
    image: PORTFOLIO_PRECLOSE,
    dark: false,
  },
  {
    category: "Government Contracting Coaching",
    signal: "GOVCON PIPELINE & FUNNEL",
    tags: ["Government Contracting", "Challenge Funnel", "GovCon CRM"],
    title: "She's Got Goals (Karwanna D)",
    role: "Architected 5-day Government Contracting challenge enrollment funnels, automated lead qualification for GovCon coaching, video onboarding delivery, and student nurture pipelines.",
    metric: "$30M+",
    metricLabel: "Client Contract Wins",
    url: "https://shesgotgoals.com/",
    image:
      "https://assets.cdn.filesafe.space/pLFrnXnIu7IgQ4WCaCfz/media/6ad7a30af9a985d42c3fbcaa.webp",
    dark: true,
  },
  {
    category: "Real Estate Ad Tech",
    signal: "AI AD ENGINE & CRM",
    tags: ["Multi-Ad Generation", "Lead CRM", "Listing Engine"],
    title: "AI Ad Machine & CRM",
    role: "Engineered automated ad testing pipelines, dynamic budget optimization, real estate CRM routing, and listing follow-up workflows.",
    metric: "From 2¢",
    metricLabel: "Cost Per Click",
    url: "https://admachine.dev/",
    image: PORTFOLIO_ADMACHINE,
    dark: true,
  },
  {
    category: "Video Training & Ops",
    signal: "VIDEO SOP ENGINE",
    tags: ["Video Onboarding", "SOP Automation", "Staff Portals"],
    title: "MultiplyME Training & SOPs",
    role: "Architected automated video onboarding workflows, QR-triggered SOP delivery systems, team training vaults, and client portal access pipelines.",
    metric: "+64%",
    metricLabel: "Faster Onboarding Cycle",
    url: "https://multiplyme.com.au/",
    image:
      "https://assets.cdn.filesafe.space/j1oGTjseMx0O1vAMfaCU/media/6abbc27e12c0bdec2caf631e.png",
    dark: false,
  },
];

function ProofCardView({ c }: { c: ProofCard }) {
  const dark = c.dark;
  return (
    <a
      href={c.url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`${c.title} — ${c.role} Visit live platform (opens in a new tab).`}
      className="revops-marquee-card"
      data-theme={dark ? "dark" : "light"}
      style={{
        background: dark ? "#071E61" : "#FFFFFF",
        color: dark ? "#FFFFFF" : "#071E61",
        border: dark
          ? "1px solid rgba(255,255,255,0.12)"
          : "1px solid rgba(8,46,103,0.09)",
        boxShadow: dark
          ? "0 18px 44px rgba(5,25,67,0.24)"
          : "0 14px 36px rgba(8,46,103,0.10)",
      }}
    >
      {/* Top media — real live-site screenshot */}
      <div
        className="revops-marquee-media"
        style={{
          background: dark
            ? "linear-gradient(135deg, #0B2A6B 0%, #051937 100%)"
            : "linear-gradient(135deg, #EAF5FF 0%, #D6ECFF 100%)",
        }}
      >
        <img
          src={c.image}
          alt={`${c.title} — live platform screenshot`}
          className="revops-marquee-shot"
          loading="lazy"
          decoding="async"
        />
      </div>

      {/* Tag row */}
      <div className="revops-marquee-tags">
        {c.tags.map((t) => (
          <span
            key={t}
            className="revops-marquee-tag"
            style={{
              background: dark ? "rgba(255,255,255,0.08)" : "#EDF7FF",
              color: dark ? "#BFD9FF" : "#0B7CF2",
              border: dark
                ? "1px solid rgba(255,255,255,0.10)"
                : "1px solid rgba(11,124,242,0.10)",
            }}
          >
            {t}
          </span>
        ))}
      </div>

      {/* Title */}
      <h3
        className="revops-marquee-title text-left"
        style={{ color: dark ? "#FFFFFF" : "#071E61" }}
      >
        {c.title}
      </h3>

      {/* What I do in this business */}
      <p
        className="revops-marquee-role text-left"
        style={{
          color: dark ? "rgba(255,255,255,0.72)" : "#536B89",
        }}
      >
        {c.role}
      </p>

      {/* Divider */}
      <div
        className="revops-marquee-rule"
        style={{
          background: dark ? "rgba(255,255,255,0.10)" : "rgba(8,46,103,0.08)",
        }}
      />

      {/* Footer link arrow */}
      <div className="revops-marquee-foot justify-end">
        <span
          className="revops-marquee-arrow"
          style={{
            background: dark ? "rgba(255,255,255,0.10)" : "#EDF7FF",
            color: dark ? "#FFFFFF" : "#0B7CF2",
          }}
        >
          <ArrowUpRight className="w-4 h-4" strokeWidth={2.4} />
        </span>
      </div>
    </a>
  );
}

export function ServiceRevOpsMarquee() {
  // Duplicate the array for a seamless infinite loop.
  const loop = [...cards, ...cards];
  return (
    <div
      className="revops-marquee"
      aria-label="Live production systems Marjohn engineered across 5 platforms"
    >
      {/* Edge fades */}
      <div
        className="revops-marquee-fade revops-marquee-fade-left"
        aria-hidden="true"
      />
      <div
        className="revops-marquee-fade revops-marquee-fade-right"
        aria-hidden="true"
      />
      <div className="revops-marquee-track">
        {loop.map((c, i) => (
          <ProofCardView key={`${c.title}-${i}`} c={c} />
        ))}
      </div>
    </div>
  );
}
